import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

interface WaveformVisualizerProps {
    analyserNode: AnalyserNode | null;
    isRecording: boolean;
    color?: string;
    height?: number;
}

const CanvasContainer = styled.div<{ $height: number }>`
    width: 100%;
    height: ${props => props.$height}px;
    background-color: #f1f5f9;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    
    canvas {
        width: 100%;
        height: 100%;
    }
`;

export function WaveformVisualizer({ analyserNode, isRecording, color = '#4CAF50', height = 60 }: WaveformVisualizerProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Setup high-DPI canvas
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        if (!analyserNode || !isRecording) {
            // Draw custom flat line when idle or paused
            ctx.clearRect(0, 0, rect.width, rect.height);
            ctx.beginPath();
            ctx.moveTo(0, rect.height / 2);
            ctx.lineTo(rect.width, rect.height / 2);
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#cbd5e1'; // gray flat line
            ctx.stroke();
            
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
            return;
        }

        const bufferLength = analyserNode.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            animationRef.current = requestAnimationFrame(draw);

            analyserNode.getByteTimeDomainData(dataArray);

            ctx.clearRect(0, 0, rect.width, rect.height);
            ctx.lineWidth = 2;
            ctx.strokeStyle = color;
            ctx.beginPath();

            const sliceWidth = rect.width / bufferLength;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                const v = dataArray[i] / 128.0;
                const y = (v * rect.height) / 2;

                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }

                x += sliceWidth;
            }

            ctx.lineTo(rect.width, rect.height / 2);
            ctx.stroke();
        };

        draw();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [analyserNode, isRecording, color]);

    return (
        <CanvasContainer $height={height}>
            <canvas ref={canvasRef} />
        </CanvasContainer>
    );
}